import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Translate } from '../../shared/LanguageService';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import { readApiErrorMessage } from './apiErrorMessage';
import FeatureFlagForm, { FeatureFlagFormState } from './FeatureFlagForm';
import { useCreateFeatureFlagMutation } from './queries/useCreateFeatureFlagMutation';
import { useUpdateFeatureFlagMutation } from './queries/useUpdateFeatureFlagMutation';
import { hasRuleErrors, RuleErrors, validateRules } from './ruleValidation';
import { FeatureFlag } from './types';

interface FeatureFlagDialogProps {
    open: boolean;
    onClose: () => void;
    onSaved: (savedFlag: FeatureFlag, isCreate: boolean) => void;
    flag: FeatureFlag | null;
}

function flagToFormState(flag: FeatureFlag | null): FeatureFlagFormState {
    if (flag === null) {
        return {
            key: '',
            description: '',
            defaultEnabled: false,
            variants: [],
            defaultVariant: null,
            rules: []
        };
    }
    return {
        key: flag.key,
        description: flag.description != null ? flag.description : '',
        defaultEnabled: flag.defaultEnabled,
        variants: flag.variants != null ? flag.variants : [],
        defaultVariant: flag.defaultVariant != null ? flag.defaultVariant : null,
        rules: flag.rules.map((rule) => ({
            id: rule.id,
            priority: rule.priority,
            ruleType: rule.ruleType,
            operator: rule.operator,
            values: rule.values,
            resultEnabled: rule.resultEnabled,
            resultVariant: rule.resultVariant != null ? rule.resultVariant : null
        }))
    };
}

export default function FeatureFlagDialog({
    open,
    onClose,
    onSaved,
    flag
}: FeatureFlagDialogProps) {
    const isEdit = flag !== null;
    const [state, setState] = React.useState<FeatureFlagFormState>(() => flagToFormState(flag));
    const [saveError, setSaveError] = React.useState<string | null>(null);
    // Validation stays hidden until the first save attempt, then tracks edits live so errors clear
    // as the admin fixes them.
    const [isValidationVisible, setIsValidationVisible] = React.useState(false);

    const createMutation = useCreateFeatureFlagMutation();
    const updateMutation = useUpdateFeatureFlagMutation();

    React.useEffect(() => {
        if (open) {
            setState(flagToFormState(flag));
            setSaveError(null);
            setIsValidationVisible(false);
        }
    }, [open, flag]);

    const isSaving = createMutation.isLoading || updateMutation.isLoading;

    const ruleErrors = React.useMemo(() => validateRules(state.rules), [state.rules]);
    const visibleRuleErrors: RuleErrors | undefined = isValidationVisible ? ruleErrors : undefined;

    const isKeyMissing = !isEdit && state.key.trim().length === 0;
    const keyError =
        isValidationVisible && isKeyMissing
            ? Translate('feature-flags/dialog/key-required-error')
            : null;

    const handleSave = async () => {
        setIsValidationVisible(true);

        if (isKeyMissing) {
            return;
        }

        // The backend rejects these outright with a 400 that doesn't say which rule is at fault,
        // so stop here and let the inline messages point at it.
        if (hasRuleErrors(ruleErrors)) {
            setSaveError(Translate('feature-flags/dialog/rules-invalid-error'));
            return;
        }

        const description = state.description.length === 0 ? null : state.description;

        try {
            let savedFlag: FeatureFlag;
            if (isEdit) {
                savedFlag = await updateMutation.mutateAsync({
                    key: flag!.key,
                    input: {
                        description,
                        defaultEnabled: state.defaultEnabled,
                        defaultVariant: state.defaultVariant,
                        rules: state.rules
                    }
                });
            } else {
                savedFlag = await createMutation.mutateAsync({
                    key: state.key.trim(),
                    description,
                    defaultEnabled: state.defaultEnabled,
                    rules: state.rules
                });
            }
            onSaved(savedFlag, !isEdit);
            onClose();
        } catch (error) {
            const errorMessage = await readApiErrorMessage(error);
            setSaveError(Translate('feature-flags/dialog/save-error', { errorMessage }));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isEdit
                        ? Translate('feature-flags/dialog/title-edit', { flagKey: flag!.key })
                        : Translate('feature-flags/dialog/title-new')}
                </DialogTitle>
                <DialogContent dividers>
                    <FeatureFlagForm
                        state={state}
                        onChange={setState}
                        isKeyEditable={!isEdit}
                        keyError={keyError}
                        ruleErrors={visibleRuleErrors}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSaving}>
                        {Translate('feature-flags/common/cancel')}
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={isSaving}>
                        {isSaving
                            ? Translate('feature-flags/dialog/saving')
                            : Translate('feature-flags/dialog/save-button')}
                    </Button>
                </DialogActions>
            </Dialog>
            <FailureSnackbar
                open={saveError !== null}
                onClose={() => setSaveError(null)}
                text={saveError != null ? saveError : ''}
            />
        </>
    );
}
